import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Typography,
    Spin,
    Row,
    Col,
    message,
    Descriptions,
    List,
    Button,
    Table,
    Input,
} from "antd";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { ExpandOutlined, CompressOutlined, InfoCircleOutlined, CopyOutlined, EditOutlined, CheckOutlined, DeleteOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import HeaderComponent from "../Components/Headers/HeaderComponent";
import SimpleCard from "../Components/Cards/SimpleCard";
import EmptyCard from "../EmptyCard";
import { removeWhiteBackground } from "../../services/utils/ARSCatalogUtils";
import { primaryShades, greyScaleTokens } from "../../theme/theme";
import FullScreenModal from "../Components/Modals/FullScreenModal";
import TryOnIcon from "../../assets/icon/Try-it-on-icon-centered.svg";
import LazyThumbnail from "./LazyThumbnail";
import Model3DLoader from "./Model3DLoader";
import { secureGetItem } from "../../data/utils";
import CustomTooltip from "../Components/Modals/CustomTooltip";
import {
    canView3DAssets,
    handleGenerateUsdz,
    handleFileUpload,
    isValidUrl,
    checkImageValidity
} from '../../services/utils/3DAssetsManager';
// Aggiungi import Firebase
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import { showSuccessNotification, showErrorNotification, showInfoNotification } from '../../utils/notifications';

const { Title, Text } = Typography;

// Colori del componente
const COLORS = {
    // Sfondi
    backgroundWhite: "#ffffff",
    backgroundTransparent: "transparent",
    backgroundSemiTransparent: "rgba(255,255,255,0.7)",

    // Testo
    textPrimary: "#000000",
    textSecondary: "rgba(0,0,0,0.45)",

    // Bordi e ombre
    border: "#d9d9d9",
    shadowLight: "rgba(0,0,0,0.05)",
    shadowMedium: "rgba(0,0,0,0.08)",
    shadowDark: "rgba(0,0,0,0.15)",

    // Stati
    selectedBackground: "#f0f5ff",

    // Componenti
    tryOnButton: greyScaleTokens.grey200,
    navigationIcon: primaryShades.primary300,
    navigationIconDisabled: primaryShades.primary100,
    modelIcon: primaryShades.primary300,
    modelIconBackground: primaryShades.primary100,
    modelIconDark: primaryShades.primary700,
};

// Dimensioni in rem
const SIZES = {
    // Spaziature
    spacingXs: "0.25rem", // 4px
    spacingS: "0.5rem", // 8px
    spacingM: "1rem", // 16px
    spacingL: "1.25rem", // 20px
    spacingXl: "1.875rem", // 30px
    spacingXxl: "3.125rem", // 50px

    // Dimensioni specifiche
    buttonSmall: "2.1875rem", // 35px
    thumbnailSize: "3.75rem", // 60px
    maxWidthGallery: "37.5rem", // 600px
    maxHeightExpanded: "62.5rem", // 1000px

    // Font sizes
    fontSmall: "0.875rem", // 14px
    fontMedium: "1rem", // 16px
    fontLarge: "2rem", // 32px
    fontXlarge: "4rem", // 64px

    // Border radius
    radiusSmall: "0.25rem", // 4px
    radiusMedium: "0.5rem", // 8px

    // Borders
    borderThin: "0.0625rem", // 1px
    borderMedium: "0.125rem", // 2px

    // Heights
    heightButton: "3.75rem", // 60px
    heightContainer: "calc(100vh - 12.5rem)", // calc(100vh - 200px)
};

const UnifiedProductDetail = ({
    // Configuration props
    apiConfig: { fetchProduct, fetchVariants, buildModelUrl, buildVtoUrl },
    routeConfig: { backRoute, catalogueRoute, onBack },
    componentName = "Product",
    productId: propProductId,
    catalogueId: propCatalogueId,
    // New prop to control 3D Assets visibility
    show3DAssets = true,
}) => {
    const { productId: paramProductId, catalogueId: paramCatalogueId } =
        useParams();
    const productId = propProductId || paramProductId;
    const catalogueId = propCatalogueId || paramCatalogueId;
    const navigate = useNavigate();
    const fromCatalogue = !!catalogueId;

    // Simplified loading state management
    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [processedImages, setProcessedImages] = useState({});
    const [selectedVariant, setSelectedVariant] = useState(null);
    const canvasRef = useRef(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [errorImages, setErrorImages] = useState({});
    const [variantImagesCache, setVariantImagesCache] = useState([]);
    const [modelUrl, setModelUrl] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);
    const [isVtoModalVisible, setIsVtoModalVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [modelPriority, setModelPriority] = useState(true); // Priorità al caricamento 3D

    // Nuovo stato per gestire il caricamento VTO
    const [vtoContent, setVtoContent] = useState(null);
    const [loadingVto, setLoadingVto] = useState(false);

    // Cache ottimizzata per immagini e modelli 3D
    const imageCache = useRef(new Map());
    const modelCache = useRef(new Map());
    const preloadedImages = useRef(new Set());

    // Funzione per pulire la cache quando necessario (evita accumulo di memoria)
    const cleanupCache = () => {
        const now = Date.now();
        const maxAge = 10 * 60 * 1000; // 10 minuti

        // Pulisci cache immagini vecchie
        for (const [key, value] of imageCache.current.entries()) {
            if (now - value.timestamp > maxAge) {
                imageCache.current.delete(key);
            }
        }

        // Pulisci cache modelli vecchi
        for (const [key, value] of modelCache.current.entries()) {
            if (now - value.timestamp > maxAge) {
                modelCache.current.delete(key);
            }
        }
    };

    // Cleanup della cache ogni 5 minuti
    useEffect(() => {
        const cleanupInterval = setInterval(cleanupCache, 5 * 60 * 1000);
        return () => clearInterval(cleanupInterval);
    }, []);

    // Funzione ottimizzata per verificare se un'immagine è caricabile con cache
    const checkImageValidityWithCache = async (imageUrl, imageKey) => {
        if (!imageUrl) {
            setErrorImages((prev) => ({ ...prev, [imageKey]: true }));
            return false;
        }

        // Controlla se l'immagine è già in cache
        if (imageCache.current.has(imageUrl)) {
            const cached = imageCache.current.get(imageUrl);
            setErrorImages((prev) => ({
                ...prev,
                [imageKey]: !cached.isValid,
            }));
            return cached.isValid;
        }

        // Utilizziamo la funzione condivisa checkImageValidity
        const isValid = await checkImageValidity(imageUrl);

        // Salviamo il risultato in cache
        imageCache.current.set(imageUrl, {
            isValid,
            timestamp: Date.now(),
        });

        setErrorImages((prev) => ({ ...prev, [imageKey]: !isValid }));
        return isValid;
    };

    // Funzione ottimizzata per il processing delle immagini con cache
    const processImage = (imgUrl, id) => {
        if (!imgUrl || errorImages[id]) return null;

        // Controlla se l'immagine processata è già in cache
        if (processedImages[id]) {
            return processedImages[id];
        }

        try {
            const result = removeWhiteBackground(
                imgUrl,
                id,
                processedImages,
                setProcessedImages
            );
            return result;
        } catch (error) {
            console.warn(`Error processing image ${imgUrl}:`, error);
            setErrorImages((prev) => ({ ...prev, [id]: true }));
            return null;
        }
    };

    // Funzione di utilità per processare immagini con gestione automatica degli errori
    const safeProcessImage = (url, key) => {
        if (!url) return;
        processImage(url, key);
    };

    // Funzione ottimizzata per preload delle immagini delle varianti (solo quando necessario)
    const preloadVariantImages = (variant) => {
        if (modelPriority) return; // Non caricare le immagini se la priorità è al 3D

        const imagesToPreload = [];

        if (variant.poster && !preloadedImages.current.has(variant.poster)) {
            imagesToPreload.push(variant.poster);
        }

        // Carica solo le prime 3 immagini aggiuntive per ottimizzare
        ["poster2", "poster3", "poster4"].slice(0, 2).forEach((posterKey) => {
            if (
                variant[posterKey] &&
                !preloadedImages.current.has(variant[posterKey])
            ) {
                imagesToPreload.push(variant[posterKey]);
            }
        });

        // Preload asincrono con ritardo per non interferire con il 3D
        imagesToPreload.forEach((imageUrl, index) => {
            setTimeout(() => {
                const img = new Image();
                img.onload = () => {
                    preloadedImages.current.add(imageUrl);
                    imageCache.current.set(imageUrl, {
                        isValid: true,
                        timestamp: Date.now(),
                    });
                };
                img.onerror = () => {
                    imageCache.current.set(imageUrl, {
                        isValid: false,
                        timestamp: Date.now(),
                    });
                };
                img.src = imageUrl;
            }, index * 100); // Ritardo progressivo per evitare sovraccarico
        });
    };

    // Funzione per caricare il contenuto VTO
    const loadVtoContent = async (product, variant) => {
        if (!buildVtoUrl || !product || !variant) {
            return;
        }

        try {
            setLoadingVto(true);
            const content = await buildVtoUrl(
                product,
                variant,
                productId,
                catalogueId
            );
            setVtoContent(content);
        } catch (error) {
            console.error("Error loading VTO content:", error);
            setVtoContent(
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "1.2rem",
                    }}
                >
                    Error loading Virtual Try-On
                </div>
            );
        } finally {
            setLoadingVto(false);
        }
    };

    // Load product data
    useEffect(() => {
        const loadProductData = async () => {
            try {
                const productData = await fetchProduct(productId);
                setProduct(productData);
            } catch (error) {
                console.error("Error loading product data:", error);
                message.error("Unable to load product data");
                setLoading(false);
            }
        };

        if (productId) {
            loadProductData();
        }
    }, [productId, fetchProduct]);

    // Load product variants with 3D model priority
    useEffect(() => {
        const loadVariants = async () => {
            if (!product || !product.id) return;

            try {
                const variantsData = await fetchVariants(product.id);
                const publishedVariants = variantsData.filter(
                    (variant) => variant.status === "Published"
                );
                setVariants(publishedVariants);

                // Select the first variant by default when variants are loaded
                if (publishedVariants.length > 0 && !selectedVariant) {
                    setSelectedVariant(publishedVariants[0]);

                    // PRIORITÀ AL MODELLO 3D - Carica prima il modello 3D
                    const modelViewerUrl = buildModelUrl(
                        product,
                        publishedVariants[0],
                        productId
                    );

                    // Cache del modello 3D
                    const cacheKey = publishedVariants[0].id;
                    modelCache.current.set(cacheKey, {
                        url: modelViewerUrl,
                        timestamp: Date.now(),
                    });

                    setModelUrl(modelViewerUrl);

                    // Carica il contenuto VTO per la variante selezionata
                    loadVtoContent(product, publishedVariants[0]);

                    // Ritarda il preload delle immagini per dare priorità al 3D
                    setTimeout(() => {
                        setModelPriority(false);
                        // Preload delle immagini solo per la variante selezionata
                        preloadVariantImages(publishedVariants[0]);
                        // Complete loading when everything is ready
                        setLoading(false);
                    }, 300); // Ritardo ridotto per un caricamento più veloce
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error loading variants:", error);
                message.error("Unable to load product variants");
                setLoading(false);
            } finally {
                setInitialLoad(false);
            }
        };

        if (product?.id) {
            loadVariants();
        }
    }, [product, productId, fetchVariants, buildModelUrl]);

    // Aggiorna l'URL del modello quando cambia la variante selezionata con cache
    useEffect(() => {
        if (selectedVariant && product) {
            // Controlla se il modello è già in cache
            const cacheKey = selectedVariant.id;
            let modelViewerUrl;
            if (modelCache.current.has(cacheKey)) {
                modelViewerUrl = modelCache.current.get(cacheKey).url;
            } else {
                modelViewerUrl = buildModelUrl(
                    product,
                    selectedVariant,
                    productId
                );
                modelCache.current.set(cacheKey, {
                    url: modelViewerUrl,
                    timestamp: Date.now(),
                });
            }

            setModelUrl(modelViewerUrl);
        }
    }, [selectedVariant, product, productId, buildModelUrl]);

    // Handle return to the appropriate page
    const handleBack = () => {
        if (onBack) {
            // Usa la callback personalizzata se fornita (per evitare remount)
            onBack();
        } else if (fromCatalogue) {
            // Navigate back to the catalogue detail page
            navigate(`${catalogueRoute}/${catalogueId}`);
        } else {
            // Navigate back to the main catalog page with state indicating return from detail
            navigate(backRoute, {
                state: { fromDetail: true },
            });
        }
    };

    // Handle variant selection ottimizzato con priorità 3D
    const handleVariantSelect = (variant) => {
        // Previeni la selezione della stessa variante
        if (selectedVariant && selectedVariant.id === variant.id) {
            return;
        }

        setSelectedVariant(variant);
        setCurrentImageIndex(0); // Reset image index when changing variant
        setModelPriority(true); // Ripristina la priorità al 3D

        // Costruisci l'URL con cache - PRIORITÀ AL 3D
        const cacheKey = variant.id;
        let modelViewerUrl;
        if (modelCache.current.has(cacheKey)) {
            modelViewerUrl = modelCache.current.get(cacheKey).url;
        } else {
            modelViewerUrl = buildModelUrl(product, variant, productId);
            modelCache.current.set(cacheKey, {
                url: modelViewerUrl,
                timestamp: Date.now(),
            });
        }

        setModelUrl(modelViewerUrl);

        // Carica il contenuto VTO per la nuova variante
        loadVtoContent(product, variant);

        // Ritarda il preload delle immagini per dare priorità al 3D
        setTimeout(() => {
            setModelPriority(false);
            preloadVariantImages(variant);
        }, 300); // Ritardo più breve per il cambio variante
    };

    // Funzioni di navigazione per le immagini
    const goToNextImage = (maxIndex) => {
        setCurrentImageIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
    };

    const goToPrevImage = () => {
        setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };

    // Effetto ottimizzato per controllare lo stato delle immagini (ora gestito individualmente)
    useEffect(() => {
        if (!selectedVariant || variantImagesCache.length === 0) {
            return;
        }
    }, [selectedVariant, variantImagesCache]);

    // Helper per verificare se un URL immagine è valido
    const isValidImageUrl = (url) => {
        return (
            url && url.trim() !== "" && url !== "undefined" && url !== "null"
        );
    };

    // Funzione per aggiungere un'immagine solo se il suo URL non è già presente
    const addUniqueImage = (url, key, variantImages, uniqueUrls) => {
        // Se l'URL è già stato aggiunto, salta
        if (uniqueUrls.has(url)) return;

        // Aggiungi l'URL al set di tracciamento
        uniqueUrls.add(url);

        // Verifica la validità dell'immagine se non è stata già verificata
        if (errorImages[key] === undefined) {
            checkImageValidityWithCache(url, key);
        }

        // Processa l'immagine solo se non è stata segnalata come problematica
        if (!errorImages[key]) {
            const imgSrc = processedImages[key] || url;

            if (!processedImages[key]) {
                // Processa l'immagine in modo asincrono
                safeProcessImage(url, key);
            }

            variantImages.push({ key, src: imgSrc, originalUrl: url });
        }
    };

    // Funzione per raccogliere le immagini della variante
    const collectVariantImages = (variant) => {
        const variantImages = [];
        const uniqueUrls = new Set();

        // Aggiungiamo l'icona 3D come prima "immagine"
        variantImages.push({ key: "model_3d", is3DModel: true });

        // Verifica e aggiungi il poster principale
        if (isValidImageUrl(variant.poster)) {
            const variantImgKey = `variant_${variant.id}_poster`;
            addUniqueImage(
                variant.poster,
                variantImgKey,
                variantImages,
                uniqueUrls
            );
        }

        // Controllo e aggiungo le altre immagini (poster2, poster3, poster4)
        ["poster2", "poster3", "poster4"].forEach((posterKey) => {
            if (isValidImageUrl(variant[posterKey])) {
                const imgKey = `variant_${variant.id}_${posterKey}`;
                addUniqueImage(
                    variant[posterKey],
                    imgKey,
                    variantImages,
                    uniqueUrls
                );
            }
        });

        return variantImages;
    };

    // UNICA GALLERY che si adatta dinamicamente al layout
    const renderUnifiedGallery = () => {
        if (!selectedVariant) return <div>No variant selected</div>;

        const variantImages = collectVariantImages(selectedVariant);

        // Aggiorna la cache delle immagini per il controllo del caricamento
        if (
            JSON.stringify(variantImagesCache) !== JSON.stringify(variantImages)
        ) {
            setVariantImagesCache(variantImages);
        }

        const maxIndex = variantImages.length - 1;

        return (
            <div
                style={{
                    textAlign: "center",
                    marginBottom: "24px",
                    width: "100%",
                }}
            >
                <div
                    style={{
                        height: "43.75rem",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {/* Galleria immagini */}
                    {variantImages.length === 0 ? (
                        <div style={{ width: "100%" }}>
                            <EmptyCard />
                        </div>
                    ) : (
                        <div
                            style={{
                                width: "100%",
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "37.5rem",
                                overflow: "hidden",
                            }}
                        >
                            {/* Pulsante Try On VTO e Expand View in posizione assoluta */}
                            {selectedVariant && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: SIZES.spacingM,
                                        right: SIZES.spacingM,
                                        zIndex: 20,
                                        display: "flex",
                                        gap: SIZES.spacingS,
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Button
                                        type="link"
                                        style={{
                                            backgroundColor: COLORS.tryOnButton,
                                            border: "none",
                                            boxShadow: "none",
                                            display: "flex",
                                            alignItems: "center",
                                            color: COLORS.textPrimary,
                                            padding: `${SIZES.spacingS} ${SIZES.spacingM}`,
                                            borderRadius: SIZES.radiusSmall,
                                        }}
                                        onClick={() => {
                                            setIsVtoModalVisible(true);
                                        }}
                                    >
                                        <img
                                            src={TryOnIcon}
                                            alt="Try On"
                                            style={{
                                                width: SIZES.buttonSmall,
                                                height: SIZES.buttonSmall,
                                                objectFit: "contain",
                                            }}
                                        />
                                        Try On VTO
                                    </Button>
                                    <Button
                                        type="text"
                                        icon={
                                            isExpanded ? (
                                                <CompressOutlined />
                                            ) : (
                                                <ExpandOutlined />
                                            )
                                        }
                                        onClick={() =>
                                            setIsExpanded(!isExpanded)
                                        }
                                        style={{
                                            fontSize: SIZES.fontSmall,
                                            padding: `${SIZES.spacingXs} ${SIZES.spacingS}`,
                                            borderRadius: SIZES.radiusSmall,
                                            border: `${SIZES.borderThin} solid ${COLORS.border}`,
                                        }}
                                    >
                                        {isExpanded
                                            ? "Compress View"
                                            : "Expand View"}
                                    </Button>
                                </div>
                            )}

                            {/* Contenitore con effetto di transizione */}
                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    position: "relative",
                                }}
                            >
                                {/* Mostra sempre le immagini disponibili, prioritizzando il 3D */}
                                {variantImages.map((image, index) => (
                                    <div
                                        key={image.key}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            opacity:
                                                index === currentImageIndex
                                                    ? 1
                                                    : 0,
                                            zIndex:
                                                index === currentImageIndex
                                                    ? 2
                                                    : 1,
                                            transition: "opacity 0.2s ease",
                                        }}
                                    >
                                        {image.is3DModel ? (
                                            <Model3DLoader
                                                modelUrl={modelUrl}
                                            />
                                        ) : (
                                            <>
                                                {/* Mostra spinner solo se l'immagine non è ancora processata e non è in errore */}
                                                {!processedImages[image.key] &&
                                                    !errorImages[image.key] && (
                                                        <div
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                width: "100%",
                                                                height: "100%",
                                                                display: "flex",
                                                                justifyContent:
                                                                    "center",
                                                                alignItems:
                                                                    "center",
                                                                zIndex: 3,
                                                                backgroundColor: COLORS.backgroundSemiTransparent,
                                                            }}
                                                        >
                                                            <Spin size="large" />
                                                        </div>
                                                    )}
                                                <img
                                                    src={image.src}
                                                    alt={`${selectedVariant.glassesName} - View ${index}`}
                                                    style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "37.5rem",
                                                        objectFit: "contain",
                                                        opacity:
                                                            processedImages[
                                                                image.key
                                                            ] ||
                                                                errorImages[
                                                                image.key
                                                                ]
                                                                ? 1
                                                                : 0.3,
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Icone di navigazione pure (Material) */}
                            {variantImages.length > 1 && (
                                <>
                                    <NavigateBeforeIcon
                                        onClick={() =>
                                            currentImageIndex > 0 &&
                                            goToPrevImage()
                                        }
                                        style={{
                                            position: "absolute",
                                            left: SIZES.spacingL,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            zIndex: 10,
                                            cursor:
                                                currentImageIndex > 0
                                                    ? "pointer"
                                                    : "not-allowed",
                                            opacity:
                                                currentImageIndex === 0
                                                    ? 0.5
                                                    : 1,
                                            fontSize: "2rem",
                                            color:
                                                currentImageIndex > 0
                                                    ? COLORS.navigationIcon
                                                    : COLORS.navigationIconDisabled,
                                            transition: "opacity 0.2s ease",
                                        }}
                                    />
                                    <NavigateNextIcon
                                        onClick={() =>
                                            currentImageIndex < maxIndex &&
                                            goToNextImage(maxIndex)
                                        }
                                        style={{
                                            position: "absolute",
                                            right: SIZES.spacingL,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            zIndex: 10,
                                            cursor:
                                                currentImageIndex < maxIndex
                                                    ? "pointer"
                                                    : "not-allowed",
                                            opacity:
                                                currentImageIndex === maxIndex
                                                    ? 0.5
                                                    : 1,
                                            fontSize: "2rem",
                                            color:
                                                currentImageIndex < maxIndex
                                                    ? COLORS.navigationIcon
                                                    : COLORS.navigationIconDisabled,
                                            transition: "opacity 0.2s ease",
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {/* Thumbnails con lazy loading individuale */}
                    {variantImages.length > 1 ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: SIZES.spacingM,
                                width: "100%",
                                maxWidth: SIZES.maxWidthGallery,
                                height: SIZES.heightButton, // Altezza fissa uguale a quella delle thumbnails
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: SIZES.spacingS,
                                    flexWrap: "wrap",
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                {variantImages.map((image, index) => (
                                    <LazyThumbnail
                                        key={image.key}
                                        image={image}
                                        index={index}
                                        isSelected={index === currentImageIndex}
                                        onClick={() =>
                                            setCurrentImageIndex(index)
                                        }
                                        processedImages={processedImages}
                                        errorImages={errorImages}
                                        onImageProcess={processImage}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : variantImages.length === 1 ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: SIZES.spacingM,
                                width: "100%",
                                maxWidth: SIZES.maxWidthGallery,
                                height: SIZES.heightButton,
                            }}
                        >
                            <Text
                                type="secondary"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                No additional images available for this variant
                            </Text>
                        </div>
                    ) : null}
                </div>
                <div>
                    <Title level={4} style={{ marginTop: SIZES.spacingL }}>
                        {selectedVariant.glassesName}
                    </Title>
                </div>
            </div>
        );
    };

    // Function to format Firebase timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";

        try {
            let date;
            if (timestamp.seconds) {
                // Firebase Timestamp
                date = new Date(timestamp.seconds * 1000);
            } else if (timestamp instanceof Date) {
                // Already a Date object
                date = timestamp;
            } else {
                // String or other format
                date = new Date(timestamp);
            }

            return date.toLocaleString("it-IT", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch (error) {
            console.error("Error formatting timestamp:", error);
            return "N/A";
        }
    };

    const [role, setRole] = useState(secureGetItem("role") || null);
    const [editingKey, setEditingKey] = useState(null);
    const [originalUrl, setOriginalUrl] = useState(null);
    const [pendingUploads, setPendingUploads] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [isGeneratingUsdz, setIsGeneratingUsdz] = useState(false);

    // Aggiungi riferimento a Firestore
    const db = getFirestore();

    // Utilizziamo la funzione condivisa canView3DAssets

    // Funzione per verificare se un campo è modificabile in base al ruolo dell'utente
    const isFieldEditable = (field) => {
        if (role === 'Modellista') {
            return selectedVariant?.assignedTo === secureGetItem("email");
        }
        return ['Admin', 'ModellerSupervisor'].includes(role);
    };

    // Funzione per aggiornare un singolo URL nel database (identica a VariantDetails.js)
    const updateSingleUrl = async (key, value) => {
        try {
            if (!selectedVariant?.id) {
                showErrorNotification("Error", "Variant ID not found");
                return;
            }

            const variantRef = doc(db, "Variants", selectedVariant.id);
            await updateDoc(variantRef, { [key]: value });

            setSelectedVariant(prevVariant => ({
                ...prevVariant,
                [key]: value
            }));

            setPendingUploads(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });

            showSuccessNotification("Save successful", "URL updated successfully");
        } catch (error) {
            console.error("Error updating URL:", error);
            showErrorNotification("Error", "Error updating URL");
        }
    };

    // Funzione per salvare un URL
    const saveUrl = async (key, newUrl) => {
        if (newUrl) {
            await updateSingleUrl(key, newUrl);
            setEditingKey(null);
        }
    };

    // Funzione per annullare un caricamento in sospeso (identica a VariantDetails.js)
    const cancelPendingUpload = (key) => {
        setPendingUploads(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        showInfoNotification("Modification cancelled", "The changes have been cancelled successfully.");
    };

    // Funzione per confermare un caricamento in sospeso (identica a VariantDetails.js)
    const confirmPendingUpload = async (key) => {
        const newUrl = pendingUploads[key];

        if (newUrl) {
            await updateSingleUrl(key, newUrl);

            // Aggiorna solo il modello 3D se è stato confermato un caricamento per urlGlobal o urlGlobalComplete
            if (key === 'urlGlobal' || key === 'urlGlobalComplete') {
                setTimeout(() => {
                    // Reset e ricarica del modello 3D
                    setModelUrl(null);
                    setTimeout(() => {
                        const modelViewerUrl = buildModelUrl(product, selectedVariant, productId);
                        setModelUrl(modelViewerUrl);
                    }, 100);
                }, 1000); // Piccolo delay per permettere il salvataggio
            }
        }
    };

    // Funzione per generare il file USDZ da un file GLB
    const handleGenerateUsdzWrapper = async (key) => {
        await handleGenerateUsdz({
            variant: selectedVariant,
            key,
            setPendingUploads,
            setIsGeneratingUsdz,
            setEditingKey
        });
    };

    // Stile per il contenitore dei bottoni
    const actionContainerStyle = {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '8px'
    };

    // Render variant details info (solo le descriptions)
    const renderVariantDetailsInfo = () => {
        if (!selectedVariant) return <div>No variant selected</div>;

        const details = [
            { key: "SKU", value: selectedVariant.skuModel || "N/A" },
            { key: "EAN", value: selectedVariant.eanCode || "N/A" },
            { key: "UPC", value: selectedVariant.upcCode || "N/A" },
            { key: "Frame Color", value: selectedVariant.frameColor || "N/A" },
            {
                key: "Lenses Color",
                value:
                    selectedVariant.lensesColor === "0" ||
                        !selectedVariant.lensesColor
                        ? "TRANSPARENT"
                        : selectedVariant.lensesColor,
            },
            {
                key: "Temple Color",
                value: selectedVariant.templeColor || "N/A",
            },
        ];

        // Informazioni dettagliate sulla taglia
        const sizeDetails = [
            { key: "Size", value: selectedVariant.size?.size || "N/A" },
            {
                key: "Bridge Size",
                value: selectedVariant.size?.bridge || "N/A",
            },
            {
                key: "Lens Width",
                value: selectedVariant.size?.lensWidth || "N/A",
            },
            {
                key: "Lens Height",
                value: selectedVariant.size?.lensHeight || "N/A",
            },
            {
                key: "Temple Length",
                value: selectedVariant.size?.templeLength || "N/A",
            },
        ];

        // Informazioni aggiuntive
        const additionalDetails = [
            { key: "Status", value: selectedVariant.status || "N/A" },
            {
                key: "Last Update",
                value: formatTimestamp(selectedVariant.lastUpdate),
            },
        ];

        // Colonne per la tabella 3D Assets
        const columnsWithIcons = [
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>Key</span>
                        <CustomTooltip title="Key of the reference image">
                            <InfoCircleOutlined style={{ marginLeft: 'clamp(0.4rem, 0.35rem + 0.25vw, 0.5rem)', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'key',
                key: 'key',
                align: 'left',
                width: '25%',
                render: (text, record) => (
                    <CustomTooltip title={`Attribute: ${record.displayName || text}`}>
                        <span>{record.displayName || text}</span>
                    </CustomTooltip>
                )
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <span>URL</span>
                        <CustomTooltip title="URL address of the reference image">
                            <InfoCircleOutlined style={{ marginLeft: 'clamp(0.4rem, 0.35rem + 0.25vw, 0.5rem)', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                dataIndex: 'url',
                key: 'url',
                render: (text, record) => {
                    const pendingUrl = pendingUploads[record.key];

                    if (editingKey === record.key) {
                        return (
                            <Input
                                defaultValue={text}
                                onChange={(e) => setSelectedVariant(prev => ({
                                    ...prev,
                                    [record.key]: e.target.value
                                }))}
                                style={{ width: '100%' }}
                                autoFocus
                            />
                        );
                    } else {
                        const displayUrl = pendingUrl || text;

                        // Utilizziamo la funzione condivisa isValidUrl

                        if (!displayUrl) {
                            return <Typography.Text type="secondary">Empty</Typography.Text>;
                        }

                        // Se è un URL valido, mostralo come link cliccabile
                        if (isValidUrl(displayUrl)) {
                            return (
                                <Typography.Link
                                    href={displayUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        display: 'inline-block',
                                        color: pendingUrl ? '#1890ff' : '#000'
                                    }}
                                >
                                    {displayUrl}
                                </Typography.Link>
                            );
                        }

                        // Altrimenti, mostra il valore esatto come testo normale
                        return (
                            <Typography.Text
                                style={{
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    display: 'inline-block',
                                    color: pendingUrl ? '#1890ff' : '#000'
                                }}
                            >
                                {displayUrl}
                            </Typography.Text>
                        );
                    }
                },
                align: 'left',
                ellipsis: true,
                width: '35%'
            },
            {
                title: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span>Actions</span>
                        <CustomTooltip title="Actions available for the item">
                            <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                        </CustomTooltip>
                    </div>
                ),
                key: 'actions',
                align: 'right',
                width: '40%',
                render: (text, record) => {
                    const pendingUrl = pendingUploads[record.key];
                    const isUploading = uploadingFiles[record.key] === true;
                    const isGenerating = isGeneratingUsdz && record.key === editingKey;

                    if (isUploading || isGenerating) {
                        return (
                            <div style={actionContainerStyle}>
                                <Spin size="small" tip={isGenerating ? "Generating..." : "Uploading..."} />
                            </div>
                        );
                    }

                    if (pendingUrl) {
                        return (
                            <div style={actionContainerStyle}>
                                <Button
                                    style={{
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a',
                                        color: 'white',
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    icon={<CheckOutlined />}
                                    onClick={() => confirmPendingUpload(record.key)}
                                    disabled={!isFieldEditable('referenceImages')}
                                />
                                <Button
                                    style={{
                                        backgroundColor: '#ff4d4f',
                                        borderColor: '#ff4d4f',
                                        color: 'white',
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    icon={<DeleteOutlined />}
                                    onClick={() => cancelPendingUpload(record.key)}
                                    disabled={!isFieldEditable('referenceImages')}
                                />
                            </div>
                        );
                    }

                    if (editingKey === record.key) {
                        return (
                            <div style={actionContainerStyle}>
                                <Button
                                    style={{
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a',
                                        color: 'white',
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    icon={<CheckOutlined />}
                                    onClick={() => saveUrl(record.key, selectedVariant[record.key])}
                                    disabled={!isFieldEditable('referenceImages')}
                                />
                                <Button
                                    style={{
                                        backgroundColor: '#ff4d4f',
                                        borderColor: '#ff4d4f',
                                        color: 'white',
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        setSelectedVariant(prev => ({
                                            ...prev,
                                            [record.key]: originalUrl
                                        }));
                                        setEditingKey(null);
                                        showInfoNotification("Modification cancelled", "The changes have been cancelled successfully.");
                                    }}
                                    disabled={!isFieldEditable('referenceImages')}
                                />
                            </div>
                        );
                    }

                    return (
                        <div style={actionContainerStyle}>
                            <CustomTooltip title="Copy URL">
                                <Button
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderColor: '#d9d9d9',
                                        color: '#000000',
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                        navigator.clipboard.writeText(record.url);
                                        showSuccessNotification("Copied", "Link copied!");
                                    }}
                                    disabled={!isFieldEditable('referenceImages')}
                                />
                            </CustomTooltip>
                            {['urlGlobal', 'urlGlobalComplete'].includes(record.key) ? (
                                <CustomTooltip title="Upload GLB file">
                                    <Button
                                        style={{
                                            backgroundColor: '#ffffff',
                                            borderColor: '#d9d9d9',
                                            color: '#000000',
                                            width: '32px',
                                            height: '32px',
                                            padding: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        icon={<PlusOutlined />}
                                        disabled={isUploading || !isFieldEditable('3dAssets')}
                                        onClick={() => {
                                            // Simulare il click sul file input nascosto
                                            const fileInput = document.getElementById(`upload-${record.key}`);
                                            if (fileInput) fileInput.click();
                                        }}
                                    />
                                </CustomTooltip>
                            ) : ['urlUsdz', 'urlUsdzComplete'].includes(record.key) ? (
                                <CustomTooltip title="Generate USDZ file">
                                    <Button
                                        style={{
                                            backgroundColor: '#ffffff',
                                            borderColor: '#d9d9d9',
                                            color: '#000000',
                                            width: '32px',
                                            height: '32px',
                                            padding: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        icon={<SyncOutlined />}
                                        onClick={() => handleGenerateUsdzWrapper(record.key)}
                                        disabled={
                                            !isFieldEditable('3dAssets') ||
                                            isGeneratingUsdz ||
                                            !selectedVariant[record.key === 'urlUsdz' ? 'urlGlobal' : 'urlGlobalComplete']
                                        }
                                    />
                                </CustomTooltip>
                            ) : (
                                <CustomTooltip title="Edit URL">
                                    <Button
                                        style={{
                                            backgroundColor: '#ffffff',
                                            borderColor: '#d9d9d9',
                                            color: '#000000',
                                            width: '32px',
                                            height: '32px',
                                            padding: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            setOriginalUrl(record.url);
                                            setEditingKey(record.key);
                                        }}
                                        disabled={!isFieldEditable('referenceImages')}
                                    />
                                </CustomTooltip>
                            )}

                            {/* Hidden file inputs for uploads */}
                            {['urlGlobal', 'urlGlobalComplete'].includes(record.key) && (
                                <input
                                    id={`upload-${record.key}`}
                                    type="file"
                                    accept=".glb"
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;

                                        await handleFileUpload({
                                            file,
                                            skuModel: selectedVariant.skuModel,
                                            recordKey: record.key,
                                            setPendingUploads,
                                            setUploadingFiles
                                        });

                                        // Reset the input
                                        e.target.value = null;
                                    }}
                                />
                            )}
                        </div>
                    );
                }
            }
        ];

        return (
            <div>
                <SimpleCard
                    title={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <span>Variant Details</span>
                        </div>
                    }
                    style={{ height: "auto" }}
                    padding="0"
                    borderRadius={SIZES.radiusMedium}
                >
                    <div style={{ padding: SIZES.spacingM }}>
                        <Descriptions
                            bordered
                            size="small"
                            column={{
                                xxl: 3,
                                xl: 2,
                                lg: 2,
                                md: 1,
                                sm: 1,
                                xs: 1,
                            }}
                            title="Variant Information"
                        >
                            {details.map((item) => (
                                <Descriptions.Item
                                    key={item.key}
                                    label={item.key}
                                >
                                    {item.value}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>

                        <Descriptions
                            bordered
                            size="small"
                            column={{
                                xxl: 3,
                                xl: 2,
                                lg: 2,
                                md: 1,
                                sm: 1,
                                xs: 1,
                            }}
                            title="Size Details"
                            style={{ marginTop: SIZES.spacingM }}
                        >
                            {sizeDetails.map((item) => (
                                <Descriptions.Item
                                    key={item.key}
                                    label={item.key}
                                >
                                    {item.value}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>

                        <Descriptions
                            bordered
                            size="small"
                            column={{
                                xxl: 3,
                                xl: 2,
                                lg: 2,
                                md: 1,
                                sm: 1,
                                xs: 1,
                            }}
                            title="Additional Information"
                            style={{ marginTop: SIZES.spacingM }}
                        >
                            {additionalDetails.map((item) => (
                                <Descriptions.Item
                                    key={item.key}
                                    label={item.key}
                                >
                                    {item.value}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>

                        {/* 3D Assets Section - visibile solo per ruoli specifici e se show3DAssets è true */}
                        {show3DAssets && canView3DAssets(role) && (
                            <div style={{ marginTop: SIZES.spacingM }}>
                                <Typography.Title level={5}>3D Assets</Typography.Title>
                                <div style={{ position: 'relative' }}>
                                    <div style={{
                                        filter: !canView3DAssets(role) ? "blur(5px)" : "none"
                                    }}>
                                        <Table
                                            dataSource={[
                                                { key: 'urlGlobal', url: selectedVariant.urlGlobal, displayName: 'Url Glb Cut' },
                                                { key: 'urlGlobalComplete', url: selectedVariant.urlGlobalComplete, displayName: 'Url Glb Complete' },
                                                { key: 'urlUsdz', url: selectedVariant.urlUsdz, displayName: 'Url Usdz Cut' },
                                                { key: 'urlUsdzComplete', url: selectedVariant.urlUsdzComplete, displayName: 'Url Usdz Complete' }
                                            ].filter(item => item.key)}
                                            columns={columnsWithIcons}
                                            pagination={false}
                                            rowKey="key"
                                            size="small"
                                            style={{ backgroundColor: "#fff", borderRadius: "12px", width: '100%' }}
                                        />
                                    </div>
                                    {!canView3DAssets(role) && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            zIndex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            color: '#000',
                                            fontSize: '1.2rem',
                                            fontWeight: 'bold'
                                        }}>
                                            You do not have permission to view this data
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </SimpleCard>
            </div>
        );
    };

    // Recupera il ruolo dell'utente da localStorage all'avvio del componente
    useEffect(() => {
        const userRole = secureGetItem("role");
        if (userRole) {
            setRole(userRole);
        }
    }, []);

    return (
        <div>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

            <HeaderComponent
                title={product?.modelName ? `${componentName} ${product.modelName} Details` : `${componentName} Details`}
                buttonIcon={
                    <NavigateBeforeIcon style={{ fontSize: "1.5rem" }} />
                }
                buttonText=""
                onButtonClick={handleBack}
                isVisible={true}
                buttonPosition="left"
            />

            {loading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: SIZES.spacingXxl,
                        flexDirection: "column",
                        height: SIZES.heightContainer,
                    }}
                >
                    <Spin size="large" />
                    <Text
                        type="secondary"
                        style={{ marginTop: SIZES.spacingM }}
                    >
                        Loading product details...
                    </Text>
                </div>
            ) : (
                <div>
                    {/* Variants Section */}
                    <div style={{ marginTop: SIZES.spacingXl }}>
                        {variants.length === 0 ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: SIZES.spacingL,
                                }}
                            >
                                <Text type="secondary">
                                    No variants available for this product
                                </Text>
                            </div>
                        ) : (
                            <>
                                <div>
                                    {/* Gallery - Full width when expanded, right column when normal */}
                                    <Row gutter={[24, 24]}>
                                        {/* Variants List (Left) - Only visible in normal mode */}
                                        <AnimatePresence mode="wait">
                                            {!isExpanded && (
                                                <Col
                                                    xs={24}
                                                    sm={24}
                                                    md={8}
                                                    key="variants-left"
                                                >
                                                    <div
                                                        style={{
                                                            position: "sticky",
                                                            top: "0",
                                                            zIndex: 1000,
                                                            maxHeight:
                                                                "calc(100vh - 8rem)",
                                                            overflowY: "auto",
                                                        }}
                                                    >
                                                        <SimpleCard
                                                            padding="0"
                                                            title="Available Variants"
                                                            style={{
                                                                height: "auto",
                                                            }}
                                                            borderRadius={
                                                                SIZES.radiusMedium
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    padding:
                                                                        SIZES.spacingM,
                                                                }}
                                                            >
                                                                <List
                                                                    itemLayout="horizontal"
                                                                    dataSource={
                                                                        variants
                                                                    }
                                                                    renderItem={(
                                                                        variant
                                                                    ) => {
                                                                        const variantImgKey = `variant_list_${variant.id}`;
                                                                        const imgSrc =
                                                                            processedImages[
                                                                            variantImgKey
                                                                            ] ||
                                                                            variant.poster;

                                                                        if (
                                                                            !processedImages[
                                                                            variantImgKey
                                                                            ] &&
                                                                            variant.poster
                                                                        ) {
                                                                            processImage(
                                                                                variant.poster,
                                                                                variantImgKey
                                                                            );
                                                                        }

                                                                        const isSelected =
                                                                            selectedVariant &&
                                                                            selectedVariant.id ===
                                                                            variant.id;

                                                                        return (
                                                                            <motion.div
                                                                                whileHover={{
                                                                                    scale: 1.02,
                                                                                }}
                                                                                whileTap={{
                                                                                    scale: 0.98,
                                                                                }}
                                                                                transition={{
                                                                                    type: "spring",
                                                                                    stiffness: 400,
                                                                                    damping: 25,
                                                                                }}
                                                                            >
                                                                                <List.Item
                                                                                    onClick={() =>
                                                                                        !isSelected &&
                                                                                        handleVariantSelect(
                                                                                            variant
                                                                                        )
                                                                                    }
                                                                                    style={{
                                                                                        cursor: isSelected
                                                                                            ? "default"
                                                                                            : "pointer",
                                                                                        backgroundColor:
                                                                                            isSelected
                                                                                                ? COLORS.selectedBackground
                                                                                                : COLORS.backgroundTransparent,
                                                                                        padding:
                                                                                            SIZES.spacingS,
                                                                                        borderRadius:
                                                                                            SIZES.radiusSmall,
                                                                                        opacity:
                                                                                            isSelected
                                                                                                ? 0.8
                                                                                                : 1,
                                                                                        transition:
                                                                                            "all 0.2s ease",
                                                                                    }}
                                                                                >
                                                                                    <List.Item.Meta
                                                                                        avatar={
                                                                                            <div
                                                                                                style={{
                                                                                                    width: 80,
                                                                                                    height: 80,
                                                                                                    display:
                                                                                                        "flex",
                                                                                                    justifyContent:
                                                                                                        "center",
                                                                                                    alignItems:
                                                                                                        "center",
                                                                                                }}
                                                                                            >
                                                                                                <img
                                                                                                    src={
                                                                                                        imgSrc
                                                                                                    }
                                                                                                    alt={
                                                                                                        variant.glassesName ||
                                                                                                        "Variant"
                                                                                                    }
                                                                                                    style={{
                                                                                                        maxWidth:
                                                                                                            "100%",
                                                                                                        maxHeight:
                                                                                                            "100%",
                                                                                                        objectFit:
                                                                                                            "contain",
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        }
                                                                                        title={
                                                                                            variant.glassesName ||
                                                                                            "Unnamed Variant"
                                                                                        }
                                                                                        description={
                                                                                            <div>
                                                                                                <div>
                                                                                                    SKU:{" "}
                                                                                                    {variant.skuModel ||
                                                                                                        "N/A"}
                                                                                                </div>
                                                                                                <div>
                                                                                                    Color:{" "}
                                                                                                    {variant.frameColor ||
                                                                                                        "N/A"}
                                                                                                </div>
                                                                                            </div>
                                                                                        }
                                                                                    />
                                                                                </List.Item>
                                                                            </motion.div>
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        </SimpleCard>
                                                    </div>
                                                </Col>
                                            )}
                                        </AnimatePresence>

                                        {/* Gallery - Full width when expanded, right column when normal */}
                                        <Col
                                            xs={24}
                                            sm={24}
                                            md={isExpanded ? 24 : 16}
                                        >
                                            <div>
                                                <SimpleCard
                                                    title={
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent:
                                                                    "space-between",
                                                                alignItems:
                                                                    "center",
                                                            }}
                                                        >
                                                            <span>
                                                                Variant Gallery
                                                            </span>
                                                        </div>
                                                    }
                                                    style={{ height: "auto" }}
                                                    padding="0"
                                                    borderRadius={
                                                        SIZES.radiusMedium
                                                    }
                                                >
                                                    <div
                                                        style={{
                                                            padding:
                                                                SIZES.spacingM,
                                                        }}
                                                    >
                                                        {renderUnifiedGallery()}
                                                    </div>
                                                </SimpleCard>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Variant Details - Shows below gallery in normal mode, with variants list in expanded mode */}
                                    <div>
                                        <Row gutter={[24, 24]}>
                                            {/* Variants List in expanded mode */}
                                            {isExpanded && (
                                                <Col xs={24} sm={24} md={8}>
                                                    <div>
                                                        <SimpleCard
                                                            padding="0"
                                                            title="Available Variants"
                                                            style={{
                                                                height: "auto",
                                                            }}
                                                            borderRadius={
                                                                SIZES.radiusMedium
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    padding:
                                                                        SIZES.spacingM,
                                                                }}
                                                            >
                                                                <List
                                                                    itemLayout="horizontal"
                                                                    dataSource={
                                                                        variants
                                                                    }
                                                                    renderItem={(
                                                                        variant
                                                                    ) => {
                                                                        const variantImgKey = `variant_list_${variant.id}`;
                                                                        const imgSrc =
                                                                            processedImages[
                                                                            variantImgKey
                                                                            ] ||
                                                                            variant.poster;

                                                                        if (
                                                                            !processedImages[
                                                                            variantImgKey
                                                                            ] &&
                                                                            variant.poster
                                                                        ) {
                                                                            processImage(
                                                                                variant.poster,
                                                                                variantImgKey
                                                                            );
                                                                        }

                                                                        const isSelected =
                                                                            selectedVariant &&
                                                                            selectedVariant.id ===
                                                                            variant.id;

                                                                        return (
                                                                            <motion.div
                                                                                whileHover={{
                                                                                    scale: 1.02,
                                                                                }}
                                                                                whileTap={{
                                                                                    scale: 0.98,
                                                                                }}
                                                                                transition={{
                                                                                    type: "spring",
                                                                                    stiffness: 400,
                                                                                    damping: 25,
                                                                                }}
                                                                            >
                                                                                <List.Item
                                                                                    onClick={() =>
                                                                                        !isSelected &&
                                                                                        handleVariantSelect(
                                                                                            variant
                                                                                        )
                                                                                    }
                                                                                    style={{
                                                                                        cursor: isSelected
                                                                                            ? "default"
                                                                                            : "pointer",
                                                                                        backgroundColor:
                                                                                            isSelected
                                                                                                ? COLORS.selectedBackground
                                                                                                : COLORS.backgroundTransparent,
                                                                                        padding:
                                                                                            SIZES.spacingS,
                                                                                        borderRadius:
                                                                                            SIZES.radiusSmall,
                                                                                        opacity:
                                                                                            isSelected
                                                                                                ? 0.8
                                                                                                : 1,
                                                                                        transition:
                                                                                            "all 0.2s ease",
                                                                                    }}
                                                                                >
                                                                                    <List.Item.Meta
                                                                                        avatar={
                                                                                            <div
                                                                                                style={{
                                                                                                    width: 80,
                                                                                                    height: 80,
                                                                                                    display:
                                                                                                        "flex",
                                                                                                    justifyContent:
                                                                                                        "center",
                                                                                                    alignItems:
                                                                                                        "center",
                                                                                                }}
                                                                                            >
                                                                                                <img
                                                                                                    src={
                                                                                                        imgSrc
                                                                                                    }
                                                                                                    alt={
                                                                                                        variant.glassesName ||
                                                                                                        "Variant"
                                                                                                    }
                                                                                                    style={{
                                                                                                        maxWidth:
                                                                                                            "100%",
                                                                                                        maxHeight:
                                                                                                            "100%",
                                                                                                        objectFit:
                                                                                                            "contain",
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        }
                                                                                        title={
                                                                                            variant.glassesName ||
                                                                                            "Unnamed Variant"
                                                                                        }
                                                                                        description={
                                                                                            <div>
                                                                                                <div>
                                                                                                    SKU:{" "}
                                                                                                    {variant.skuModel ||
                                                                                                        "N/A"}
                                                                                                </div>
                                                                                                <div>
                                                                                                    Color:{" "}
                                                                                                    {variant.frameColor ||
                                                                                                        "N/A"}
                                                                                                </div>
                                                                                            </div>
                                                                                        }
                                                                                    />
                                                                                </List.Item>
                                                                            </motion.div>
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        </SimpleCard>
                                                    </div>
                                                </Col>
                                            )}
                                            <Col
                                                xs={24}
                                                sm={24}
                                                md={isExpanded ? 16 : 16}
                                                offset={isExpanded ? 0 : 8}
                                            >
                                                {renderVariantDetailsInfo()}
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Virtual Try-On Modal */}
            {selectedVariant && (
                <FullScreenModal
                    visible={isVtoModalVisible}
                    onClose={() => setIsVtoModalVisible(false)}
                    title="Virtual Try On"
                    iframe={true}
                    closeButtonPosition="top"
                    padding="0"
                >
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {loadingVto ? (
                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    flexDirection: "column",
                                }}
                            >
                                <Spin size="large" />
                                <Text
                                    type="secondary"
                                    style={{ marginTop: SIZES.spacingM }}
                                >
                                    Loading Virtual Try-On...
                                </Text>
                            </div>
                        ) : vtoContent ? (
                            vtoContent
                        ) : (
                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    fontSize: "1.2rem",
                                }}
                            >
                                Virtual Try-On not available
                            </div>
                        )}
                    </div>
                </FullScreenModal>
            )}
        </div>
    );
};

export default UnifiedProductDetail;
